import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { NotFound } from "./NotFound";
import { PageSkeleton } from "../components/PageSkeleton";
import { PageHero } from "../components/subcomponents/PageHero";
import { apiGet } from "../utils/api";
import { useLanguage } from "../contexts/LanguageContext";
import { pickLocalized, stripHtml } from "../utils/locale";
import { routes } from "../utils/utils.routes";
import { Colors } from "../utils/utils.colors";
import heroImage from "../assets/images/hero-image.png";
import { FiArrowLeft } from "react-icons/fi";

const BackI = FiArrowLeft as any;
const SITE_URL = "https://ulpgl.net";

export const FaqDetail: React.FC = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const [item, setItem] = useState<any | null>(undefined as any);
  const [others, setOthers] = useState<any[]>([]);

  useEffect(() => {
    if (!slug) return;
    setItem(undefined as any);
    apiGet(`/faqs/slug/${slug}`)
      .then((d) => setItem(d.item))
      .catch(() => setItem(null));
  }, [slug]);

  useEffect(() => {
    apiGet("/faqs")
      .then((d) => setOthers((d.rows || []).filter((f: any) => f.slug !== slug).slice(0, 6)))
      .catch(() => setOthers([]));
  }, [slug]);

  if (item === undefined) return <PageSkeleton />;
  if (item === null) return <NotFound />;

  const question = pickLocalized(item, "question", lang);
  const answerHtml = pickLocalized(item, "answer", lang);
  const answerText = stripHtml(answerHtml);
  const pageUrl = `${SITE_URL}${routes.FAQ}/${slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answerText,
        },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{question} | FAQ ULPGL</title>
        <meta name="description" content={answerText.slice(0, 160)} />

        <meta property="og:title" content={question} />
        <meta property="og:description" content={answerText.slice(0, 160)} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ULPGL - Goma" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={question} />
        <meta name="twitter:description" content={answerText.slice(0, 160)} />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <PageHero
        imageCover={heroImage}
        eyebrow="FAQ"
        title={question}
        tall
        breadcrumbs={[
          { label: "Accueil", href: routes.HOME },
          { label: lang === "en" ? "FAQ" : "FAQ", href: routes.FAQ },
          { label: question },
        ]}
      />

      <div className="content-detail padding-x is-layout-constrained">
        <Link to={routes.FAQ} className="content-detail__back pt-3">
          <BackI size={16} /> {lang === "en" ? "Back to FAQ" : "Retour à la FAQ"}
        </Link>

        <div className={others.length > 0 ? "faq-detail__layout" : ""}>
          <div className="content-detail__main" style={{ background: "white" }}>
            <h1 style={{ color: Colors.darkColor, fontSize: 26, fontWeight: 700, margin: "0 0 1.25rem" }}>
              {question}
            </h1>
            <div
              className="rich-content"
              dangerouslySetInnerHTML={{ __html: answerHtml }}
            />
          </div>

          {others.length > 0 && (
            <aside className="faq-detail__aside">
              <div className="content-detail__card">
                <h3>{lang === "en" ? "Other questions" : "Autres questions"}</h3>
                <ul className="content-detail__links">
                  {others.map((o) => (
                    <li key={o.id}>
                      <Link to={`${routes.FAQ}/${o.slug}`}>
                        {pickLocalized(o, "question", lang)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  );
};
